import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandlerFn,
  HttpInterceptorFn,
  HttpRequest,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, catchError, finalize, shareReplay, switchMap, throwError } from 'rxjs';
import { PartnerService, PartnerSession } from './partner.service';
import { TokenStorageService } from './token-storage.service';

/** Partner area requests; its login, password and refresh endpoints are public. */
export function isPartnerRequest(url: string): boolean {
  return url.includes('/partner/') && !url.includes('/partner/auth/');
}

/** One refresh at a time: parallel 401s wait for the same one. */
let refreshInFlight: Observable<PartnerSession> | null = null;

export function refreshPartnerSession(partnerService: PartnerService): Observable<PartnerSession> {
  if (!refreshInFlight) {
    refreshInFlight = partnerService.refresh().pipe(
      finalize(() => (refreshInFlight = null)),
      shareReplay(1),
    );
  }
  return refreshInFlight;
}

/**
 * Adds the partner token to partner requests and renews it once on a 401. The
 * company interceptor (authInterceptor) leaves these requests alone.
 */
export const partnerAuthInterceptor: HttpInterceptorFn = (
  request: HttpRequest<unknown>,
  next: HttpHandlerFn,
): Observable<HttpEvent<unknown>> => {
  if (!isPartnerRequest(request.url)) return next(request);

  const tokenStorage = inject(TokenStorageService);
  const partnerService = inject(PartnerService);
  const router = inject(Router);

  const token = tokenStorage.getPartnerAccessToken();
  return next(withToken(request, token)).pipe(
    catchError((error) => {
      if (!(error instanceof HttpErrorResponse) || error.status !== 401) {
        return throwError(() => error);
      }
      return refreshPartnerSession(partnerService).pipe(
        catchError((refreshError) => {
          if (refreshError instanceof HttpErrorResponse && refreshError.status === 401) {
            partnerService.clear();
            router.navigate(['/partner/login']);
          }
          return throwError(() => refreshError);
        }),
        switchMap((session) => next(withToken(request, session.access_token))),
      );
    }),
  );
};

function withToken(request: HttpRequest<unknown>, token: string | null): HttpRequest<unknown> {
  return request.clone({
    setHeaders: token ? { Authorization: `Bearer ${token}` } : {},
    withCredentials: true,
  });
}
